import { useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { logoutUser } from '@/actions/authActions'
import { selectIsReactivatedUser } from '@/selectors/user.selectors'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsReset } from '@/features/amplitude'
import { selectCanOptIntoPreviousTaxYear } from '@/featuresV1/Admin/AnnualTaxDetails/annualTaxDetails.selector'
import {
  SIGNUP_STEPS,
  SIGNUP_STEPS_2024,
  SIGNUP_STEPS_FREE_TRIAL_2024,
} from '../helpers'

import { DesktopHeader } from './DesktopHeader'
import { MobileHeader } from './MobileHeader'
import { Progress } from '@/ui'

export const SignupHeaderLogic = ({ currentStep }: { currentStep: number }) => {
  const [searchParams] = useSearchParams()
  const freeTrialSearchParamValue = searchParams.get('trial')
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const basicPlanSearchParamValue = searchParams.get('subscription')

  const canOptIntoPreviousYearTaxes = useReselector(
    selectCanOptIntoPreviousTaxYear
  )

  const useBasicTypePlan =
    basicPlanSearchParamValue === 'new' || basicPlanSearchParamValue === 'basic'

  const progress = useMemo(() => {
    let steps = SIGNUP_STEPS

    if (freeTrialSearchParamValue) {
      steps = SIGNUP_STEPS_FREE_TRIAL_2024
    } else if (useBasicTypePlan) {
      steps = SIGNUP_STEPS
    } else if (canOptIntoPreviousYearTaxes) {
      steps = SIGNUP_STEPS_2024
    }

    const totalSteps = steps.length

    if (isMobile) {
      return (
        <Progress
          variant="segmented"
          numberOfSegments={totalSteps}
          activeSegment={currentStep}
        />
      )
    }

    return (
      <Progress
        variant="segmented"
        numberOfSegments={steps.length}
        activeSegment={currentStep}
        segmentTitles={steps}
      />
    )
  }, [
    freeTrialSearchParamValue,
    canOptIntoPreviousYearTaxes,
    currentStep,
    isMobile,
    useBasicTypePlan,
  ])

  return progress
}

export const SignupHeader = ({
  currentStep,
  hideProgress,
}: {
  currentStep?: number
  hideProgress?: boolean
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const resetAnalytics = useAnalyticsReset()
  const reactivatingUser = useReselector(selectIsReactivatedUser)

  /* step is 0 if on account creation page */
  const loggedIn = currentStep !== 0

  const handleLogoutClick = useCallback(async () => {
    resetAnalytics()
    await logoutUser()(dispatch)
    localStorage.removeItem('targetLink')
    navigate('/login')
  }, [resetAnalytics, navigate, dispatch])

  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)

  return isMobile ? (
    <MobileHeader
      currentStep={currentStep || 0}
      loggedIn={loggedIn}
      handleLogoutClick={handleLogoutClick}
      reactivating={reactivatingUser}
      hideProgress={hideProgress}
    />
  ) : (
    <DesktopHeader
      loggedIn={loggedIn}
      handleLogoutClick={handleLogoutClick}
      currentStep={currentStep || 0}
      reactivating={reactivatingUser}
      hideProgress={hideProgress}
    />
  )
}
